import { useShortCode, useBusinessData } from '@/hooks/use-local-storage';
import { useGetDashboardQuery, useExportDashboardMutation } from '@/services/dashboardApi';
import { format } from 'date-fns';
import {
  ArrowUpRight, ArrowDownRight, Briefcase, Clock, MapPin, TrendingUp, Users, 
  Wrench, ClipboardList, AlertCircle, Boxes, Truck, HardHat, Download, 
  Loader2, BarChart3, Target, Info
} from 'lucide-react';
import {
  Card, CardContent, CardDescription, CardHeader, CardTitle,
} from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import {
  AreaChart, Area, BarChart, Bar, PieChart, Pie,
  ResponsiveContainer, XAxis, YAxis, Tooltip as RechartsTooltip, Legend, CartesianGrid, Cell
} from 'recharts';
import { useState } from 'react';
import { toast } from '@/components/ui/use-toast';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { UserNav } from '@/components/user-nav';
import ThemeSwitch from '@/components/theme-switch';
import { Layout } from '@/components/custom/layout';
import SelectTimeFrame from '@/components/date-picker/select-time';
import { TimeFrameType } from '@/@types/dashboard';
import { AgentsNotification } from '@/components/agents-notification';
import { Chatbot } from '@/components/chatbot';
import { WelcomeMessage } from '@/components/welcome-message';
import { motion } from 'framer-motion';
import { OnboardingBanner } from '@/components/onboarding-banner';
import { MobileOnboardingCTA } from '@/components/mobile-onboarding-cta';
import { NotificationStatus } from '@/components/NotificationStatus';
import { PWAInstallPrompt } from '@/components/pwa/PWAInstallPrompt';
import { VoiceAIAgent } from '@/components/VoiceAIAgent';

export default function Dashboard() {
  const encodedShortCode = useShortCode();
  const businessData = useBusinessData();
  const operationType = businessData?.operationType || 'hybrid';
  
  // Determine content visibility
  const showFieldContent = operationType === 'field_service' || operationType === 'hybrid';
  const showInHouseContent = operationType === 'in_house' || operationType === 'hybrid';
  
  const [timeFrame, setTimeFrame] = useState<TimeFrameType>('monthly');
  const { data, isLoading, isFetching } = useGetDashboardQuery({
    venue_short_code: encodedShortCode,
    time_frame: timeFrame,
  });

  const [exportDashboard, { isLoading: isExporting }] = useExportDashboardMutation();
  
  const handleExport = async () => {
    try {
      const blob = await exportDashboard({
        venue_short_code: encodedShortCode,
        time_frame: timeFrame
      }).unwrap();

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `dashboard-report-${timeFrame}-${format(new Date(), 'yyyy-MM-dd')}.xlsx`;

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast({
        title: "Export Successful",
        description: "Your dashboard report has been downloaded."
      });
    } catch (error) {
      console.error('Export failed:', error);
      toast({
        title: "Export Failed",
        description: "Failed to generate the dashboard report. Please try again.",
        variant: "destructive"
      });
    }
  };
  
  // Empty state component
  const EmptyState = ({ message = "No data available", icon: Icon = AlertCircle }) => (
    <div className="flex flex-col items-center justify-center h-48 px-4 text-center">
      <Icon className="h-10 w-10 text-muted-foreground mb-4 opacity-40" />
      <p className="text-sm text-muted-foreground mb-2">{message}</p>
      <Button variant="outline" size="sm" onClick={() => setTimeFrame('monthly')}>
        Try different time period
      </Button>
    </div>
  );

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.05
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.3
      }
    }
  };

  if (isLoading) {
    return (
      <Layout>
        <Layout.Body>
          <div className="flex h-screen items-center justify-center">
            <div className="text-center space-y-3">
              <Loader2 className="h-16 w-16 animate-spin text-primary mx-auto" />
              <h3 className="text-lg font-medium text-muted-foreground">Loading your dashboard...</h3>
              <p className="text-sm text-muted-foreground">Gathering latest insights...</p>
            </div>
          </div>
        </Layout.Body>
      </Layout>
    );
  }

  return (
    <Layout>
      <Layout.Header sticky className="border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <WelcomeMessage/>
        <div className='ml-auto flex items-center space-x-2'>
          <ThemeSwitch />
          <UserNav />
        </div>
      </Layout.Header>

     

    
  {/* Mobile Onboarding CTA - Only shows for mobile/PWA users who haven't dismissed it */}
  <MobileOnboardingCTA />

      <Layout.Body className="space-y-6 p-6 pb-16">
      <NotificationStatus />
      <PWAInstallPrompt />
        <TooltipProvider>
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="space-y-6"
          >
            {/* Enhanced Page Header */}
            <motion.div variants={itemVariants} className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <h1 className="text-2xl font-bold tracking-tight">
                    {operationType === 'field_service' ? 'Field Operations Dashboard' :
                     operationType === 'in_house' ? 'Project Management Dashboard' :
                     'Business Dashboard'}
                    {isFetching && <span className="ml-2 text-sm text-muted-foreground">(Refreshing...)</span>}
                  </h1>
                  <Tooltip>
                    <TooltipTrigger>
                      <Info className="h-5 w-5 text-muted-foreground hover:text-foreground transition-colors" />
                    </TooltipTrigger>
                    <TooltipContent>
                      <p className="max-w-xs">Real-time insights into your business operations. Use the time frame selector to view different periods.</p>
                    </TooltipContent>
                  </Tooltip>
                </div>
                <p className="text-muted-foreground">
                  {businessData?.name || 'Your Business'} • {format(new Date(), 'MMMM d, yyyy')}
                </p>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-2">
                <div className={`flex ${operationType === 'hybrid' ? 'w-full gap-2' : ''}`}>
                  <SelectTimeFrame 
                    timeFrame={timeFrame as TimeFrameType} 
                    setTimeFrame={setTimeFrame} 
                    className={operationType === 'hybrid' ? 'flex-1 sm:w-[180px]' : 'w-full sm:w-[180px]'}
                  />
                  
                  {operationType === 'hybrid' && (
                    <Select defaultValue="all">
                      <SelectTrigger>
                        <SelectValue placeholder="View" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Operations</SelectItem>
                        <SelectItem value="field">Field Services Only</SelectItem>
                        <SelectItem value="inhouse">In-House Only</SelectItem>
                      </SelectContent>
                    </Select>
                  )}
                </div>
                
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      className="w-full sm:w-auto"
                      onClick={handleExport}
                      disabled={isExporting}
                    >
                      {isExporting ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Exporting...
                        </>
                      ) : (
                        <>
                          <Download className="mr-2 h-4 w-4" />
                          Export Data
                        </>
                      )}
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Export dashboard data to Excel</p>
                  </TooltipContent>
                </Tooltip>
              </div>
            </motion.div>

            <motion.div variants={itemVariants}>
  <OnboardingBanner />
</motion.div>


            {/* Insights Alert */}
            <motion.div variants={itemVariants}>
  <Alert className="border-blue-200 bg-blue-50/50">
    <BarChart3 className="h-4 w-4 text-blue-600" />
    <AlertDescription className="mt-1.5 text-blue-700">
      <div className="flex items-center">
        <span>
          <strong>Business Insights:</strong> {data?.overview.totalStaff.count || 0} active staff members driving{' '}
          {data?.overview.averageProductivity.percentage}% productivity with {data?.overview.tasksCompleted.count} tasks completed this period.
        </span>
      </div>
    </AlertDescription>
  </Alert>
</motion.div>

            {/* Enhanced Overview Cards */}
            <motion.div variants={itemVariants} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* Total Staff Card */}
              <Card className="border-l-4 border-l-blue-500 hover:shadow-md transition-shadow">
                <CardHeader className="pb-2">
                  <div className="flex justify-between items-center">
                    <CardTitle className="text-sm font-medium">Total Staff</CardTitle>
                    <Users className="h-4 w-4 text-blue-600" />
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{data?.overview.totalStaff.count || 0}</div>
                  <div className="flex items-center mt-2 text-xs">
                    {(data?.overview.totalStaff.change || '').includes('+') ? (
                      <ArrowUpRight className="h-3 w-3 text-emerald-500 mr-1" />
                    ) : (data?.overview.totalStaff.change || '').includes('-') ? (
                      <ArrowDownRight className="h-3 w-3 text-rose-500 mr-1" />
                    ) : null}
                    <span className={
                      (data?.overview.totalStaff.change || '').includes('+') ? 'text-emerald-500' :
                      (data?.overview.totalStaff.change || '').includes('-') ? 'text-rose-500' :
                      'text-muted-foreground'
                    }>
                      {data?.overview.totalStaff.change || 'No change'}
                    </span>
                  </div>
                </CardContent>
              </Card>

              {/* Productivity Card */}
              <Card className="border-l-4 border-l-green-500 hover:shadow-md transition-shadow">
                <CardHeader className="pb-2">
                  <div className="flex justify-between items-center">
                    <CardTitle className="text-sm font-medium">Productivity</CardTitle>
                    <Target className="h-4 w-4 text-green-600" />
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{data?.overview.averageProductivity.percentage || 0}%</div>
                  <Progress
                    value={parseInt(data?.overview.averageProductivity.percentage || '0')}
                    className="h-2 mt-2"
                  />
                  <div className="flex items-center mt-2 text-xs">
                    {(data?.overview.averageProductivity.change || '').includes('increase') ? (
                      <ArrowUpRight className="h-3 w-3 text-emerald-500 mr-1" />
                    ) : (data?.overview.averageProductivity.change || '').includes('decrease') ? (
                      <ArrowDownRight className="h-3 w-3 text-rose-500 mr-1" />
                    ) : null}
                    <span className={
                      (data?.overview.averageProductivity.change || '').includes('increase') ? 'text-emerald-500' :
                      (data?.overview.averageProductivity.change || '').includes('decrease') ? 'text-rose-500' :
                      'text-muted-foreground'
                    }>
                      {data?.overview.averageProductivity.change || 'No change'}
                    </span>
                  </div>
                </CardContent>
              </Card>

              {/* Tasks Completed Card */}
              <Card className="border-l-4 border-l-purple-500 hover:shadow-md transition-shadow">
                <CardHeader className="pb-2">
                  <div className="flex justify-between items-center">
                    <CardTitle className="text-sm font-medium">
                      {showFieldContent && !showInHouseContent ? 'Services Completed' : 'Tasks Completed'}
                    </CardTitle>
                    <ClipboardList className="h-4 w-4 text-purple-600" />
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{data?.overview.tasksCompleted.count || 0}</div>
                  <div className="flex items-center mt-2 text-xs">
                    {(data?.overview.tasksCompleted.change || '').includes('increase') ? (
                      <ArrowUpRight className="h-3 w-3 text-emerald-500 mr-1" />
                    ) : (data?.overview.tasksCompleted.change || '').includes('decrease') ? (
                      <ArrowDownRight className="h-3 w-3 text-rose-500 mr-1" />
                    ) : null}
                    <span className={
                      (data?.overview.tasksCompleted.change || '').includes('increase') ? 'text-emerald-500' :
                      (data?.overview.tasksCompleted.change || '').includes('decrease') ? 'text-rose-500' :
                      'text-muted-foreground'
                    }>
                      {data?.overview.tasksCompleted.change || 'No change'}
                    </span>
                  </div>
                </CardContent>
              </Card>

              {/* Active Projects Card */}
              <Card className="border-l-4 border-l-orange-500 hover:shadow-md transition-shadow">
                <CardHeader className="pb-2">
                  <div className="flex justify-between items-center">
                    <CardTitle className="text-sm font-medium">Active Projects</CardTitle>
                    <Briefcase className="h-4 w-4 text-orange-600" />
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{data?.overview.activeProjects.count || 0}</div>
                  <div className="flex items-center mt-2 text-xs text-muted-foreground">
                    <span>{data?.overview.activeProjects.completed || 'No projects completed'}</span>
                  </div>
                </CardContent>
              </Card>
            </motion.div>

            {/* Enhanced Tabs with Improved Content */}
            <motion.div variants={itemVariants}>
              <Tabs defaultValue="overview" className="space-y-4">
              <div className="overflow-x-auto pb-2">
  <TabsList className="flex w-max min-w-full md:w-full md:justify-start">
    <TabsTrigger value="overview" className="flex items-center gap-2 whitespace-nowrap">
      <BarChart3 className="h-4 w-4" />
      Overview
    </TabsTrigger>
    <TabsTrigger value="performance" className="flex items-center gap-2 whitespace-nowrap">
      <Target className="h-4 w-4" />
      Performance
    </TabsTrigger>
    {showFieldContent && 
      <TabsTrigger value="field" className="whitespace-nowrap">
        <span className="flex items-center gap-2">
          <Truck className="h-4 w-4" />
          Field Ops
        </span>
      </TabsTrigger>
    }
    {showInHouseContent && 
      <TabsTrigger value="projects" className="whitespace-nowrap">
        <span className="flex items-center gap-2">
          <Briefcase className="h-4 w-4" />
          Projects
        </span>
      </TabsTrigger>
    }
    <TabsTrigger value="tasks" className="flex items-center gap-2 whitespace-nowrap">
      <ClipboardList className="h-4 w-4" />
      Tasks
    </TabsTrigger>
  </TabsList>
</div>

                <TabsContent value="overview" className="space-y-4">
  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
    {/* Productivity Chart */}
    <Card className="overflow-hidden">
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <TrendingUp className="h-5 w-5" />
          Productivity Trend
        </CardTitle>
        <CardDescription>
          {timeFrame === 'weekly' ? 'Last 7 days' : 
           timeFrame === 'monthly' ? 'Last 30 days' : 
           timeFrame === 'quarterly' ? 'Last quarter' : 'Last year'} performance metrics
        </CardDescription>
      </CardHeader>
      <CardContent className="h-[300px]">
        {data?.overview.productivityTrend?.dates?.length && data?.overview.productivityTrend?.dates?.length > 0 ? (
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart
              data={data?.overview.productivityTrend?.dates?.map((date, i) => ({
                date: format(new Date(date), "MMM d"),
                actual: data?.overview.productivityTrend.actual[i],
                expected: data?.overview.productivityTrend.expected[i],
              }))}
              margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
            >
              <defs>
                <linearGradient id="colorActual" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#8884d8" stopOpacity={0.8}/>
                  <stop offset="95%" stopColor="#8884d8" stopOpacity={0.1}/>
                </linearGradient>
                <linearGradient id="colorExpected" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#82ca9d" stopOpacity={0.8}/>
                  <stop offset="95%" stopColor="#82ca9d" stopOpacity={0.1}/>
                </linearGradient>
              </defs>
              <XAxis dataKey="date" />
              <YAxis />
              <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
              <RechartsTooltip 
                contentStyle={{
                  backgroundColor: 'hsl(var(--background))',
                  border: '1px solid hsl(var(--border))',
                  borderRadius: '8px'
                }}
              />
              <Area 
                type="monotone" 
                dataKey="actual" 
                stroke="#8884d8" 
                fillOpacity={1} 
                fill="url(#colorActual)" 
                name="Actual" 
                strokeWidth={2}
              />
              <Area 
                type="monotone" 
                dataKey="expected" 
                stroke="#82ca9d" 
                fillOpacity={1} 
                fill="url(#colorExpected)" 
                name="Expected"
                strokeWidth={2}
              />
            </AreaChart>
          </ResponsiveContainer>
        ) : (
          <EmptyState message="No productivity data available for this time period" />
        )}
      </CardContent>
    </Card>

        <VoiceAIAgent />
    {/* Task Completion Status */}
    <Card className="overflow-hidden">
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <ClipboardList className="h-5 w-5" />
          {showFieldContent && !showInHouseContent 
            ? 'Service Completion Status' 
            : 'Task Completion Status'}
        </CardTitle>
        <CardDescription>Status distribution of all work items</CardDescription>
      </CardHeader>
      <CardContent className="h-[300px]">
        {data?.overview.taskCompletionStatus ? (
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={[
                  {
                    name: 'Completed',
                    value: parseInt(data?.overview.taskCompletionStatus.completed || '0'),
                  },
                  {
                    name: 'In Progress',
                    value: parseInt(data?.overview.taskCompletionStatus.inProgress || '0'),
                  },
                  {
                    name: 'Not Started',
                    value: parseInt(data?.overview.taskCompletionStatus.notStarted || '0'),
                  },
                ]}
                cx="50%"
                cy="50%"
                innerRadius={60}
                outerRadius={100}
                paddingAngle={2}
                dataKey="value"
                label={({name, percent}) => `${name} ${(percent * 100).toFixed(0)}%`}
                labelLine={false}
              >
                {[
                  { fill: '#16a34a' }, // green
                  { fill: '#2563eb' }, // blue
                  { fill: '#dc2626' }, // red
                ].map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.fill} />
                ))}
              </Pie>
              <RechartsTooltip 
                formatter={(value) => [`${value}`, 'Count']} 
                contentStyle={{
                  backgroundColor: 'hsl(var(--background))',
                  border: '1px solid hsl(var(--border))',
                  borderRadius: '8px'
                }}
              />
              <Legend verticalAlign="bottom" height={36} />
            </PieChart>
          </ResponsiveContainer>
        ) : (
          <EmptyState message="No status data available for this time period" />
        )}
      </CardContent>
    </Card>
  </div>

  {/* Field-specific content for field service businesses */}
  {showFieldContent && (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      <Card className="border border-blue-200">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <MapPin className="h-5 w-5" />
            Field Operations
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <MapPin className="h-4 w-4 mr-2 text-muted-foreground" />
              <span className="text-sm">Active Service Areas</span>
            </div>
            <Badge variant="outline">{data?.fieldOperations?.serviceAreas || 0}</Badge>
          </div>
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <HardHat className="h-4 w-4 mr-2 text-muted-foreground" />
              <span className="text-sm">Field Personnel</span>
            </div>
            <Badge variant="outline">{data?.fieldOperations?.personnel || 0}</Badge>
          </div>
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <Truck className="h-4 w-4 mr-2 text-muted-foreground" />
              <span className="text-sm">Equipment Deployed</span>
            </div>
            <Badge variant="outline">{data?.fieldOperations?.equipment || 0}</Badge>
          </div>
        </CardContent>
      </Card>
      
      {/* Service Metrics Card */}
      <Card className="col-span-2">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Target className="h-5 w-5" />
            Service Performance
          </CardTitle>
        </CardHeader>
        <CardContent className="h-[180px]">
          {data?.serviceMetrics ? (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={[
                { name: 'Completion Rate', value: data?.serviceMetrics?.completionRate || 0 },
                { name: 'On-Time Rate', value: data?.serviceMetrics?.onTimeRate || 0 },
                { name: 'Satisfaction', value: data?.serviceMetrics?.satisfactionRate || 0 }
              ]}>
                <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                <XAxis dataKey="name" />
                <YAxis domain={[0, 100]} />
                <RechartsTooltip 
                  formatter={(value) => [`${value}%`, '']} 
                  contentStyle={{
                    backgroundColor: 'hsl(var(--background))',
                    border: '1px solid hsl(var(--border))',
                    borderRadius: '8px'
                  }}
                />
                <Bar dataKey="value" fill="#8884d8" radius={[4, 4, 0, 0]}>
                  {[
                    { value: data?.serviceMetrics?.completionRate || 0, fill: '#16a34a' },
                    { value: data?.serviceMetrics?.onTimeRate || 0, fill: '#2563eb' },
                    { value: data?.serviceMetrics?.satisfactionRate || 0, fill: '#8884d8' }
                  ].map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.fill} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <EmptyState message="No service metrics available" icon={Wrench} />
          )}
        </CardContent>
      </Card>
    </div>
  )}

  {/* Project-specific content for in-house businesses */}
  {showInHouseContent && (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Briefcase className="h-5 w-5" />
            Project Progress
          </CardTitle>
        </CardHeader>
        <CardContent>
          {data?.projectTimeline?.projects?.length && data?.projectTimeline?.projects?.length > 0 ? (
            <div className="space-y-4">
              {data.projectTimeline.projects.slice(0, 4).map((project, i) => (
                <div key={i} className="space-y-1">
                  <div className="flex justify-between">
                    <span className="text-sm font-medium">{project.name}</span>
                    <span className="text-sm text-muted-foreground">{project.completion}%</span>
                  </div>
                  <Progress value={project.completion} className="h-2" />
                  <div className="flex justify-between text-xs text-muted-foreground">
                    <span>{project.startDate}</span>
                    <span>{project.endDate}</span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <EmptyState message="No active projects" icon={Briefcase} />
          )}
        </CardContent>
      </Card>
      
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Users className="h-5 w-5" />
            Department Performance
          </CardTitle>
        </CardHeader>
        <CardContent className="h-[220px]">
          {data?.departmentMetrics?.departments?.length && data?.departmentMetrics?.departments?.length > 0 ? (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart 
                data={data?.departmentMetrics?.departments} 
                layout="vertical"
                margin={{ left: 100 }}
              >
                <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                <XAxis type="number" domain={[0, 100]} />
                <YAxis 
                  type="category" 
                  dataKey="name" 
                  width={90}
                  tick={{ fontSize: 12 }} 
                />
                <RechartsTooltip 
                  formatter={(value) => [`${value}%`, '']} 
                  contentStyle={{
                    backgroundColor: 'hsl(var(--background))',
                    border: '1px solid hsl(var(--border))',
                    borderRadius: '8px'
                  }}
                />
                <Legend />
                <Bar 
                  dataKey="productivity" 
                  name="Productivity %" 
                  fill="#8884d8"
                  radius={[0, 4, 4, 0]}
                />
                <Bar 
                  dataKey="target" 
                  name="Target %" 
                  fill="#82ca9d"
                  radius={[0, 4, 4, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <EmptyState message="No department metrics available" icon={Users} />
          )}
        </CardContent>
      </Card>
    </div>
  )}
</TabsContent>

                {/* Performance Tab */}
                <TabsContent value="performance" className="space-y-4">
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Target className="h-5 w-5" />
                        Team Performance
                      </CardTitle>
                      <CardDescription>Staff productivity and performance metrics</CardDescription>
                    </CardHeader>
                    <CardContent className="max-h-[600px] overflow-y-auto pr-2">
                      {data?.performance?.length && data?.performance?.length > 0 ? (
                        <div className="space-y-6">
                          {data?.performance?.map((performer, index) => (
                            <div key={index} className="flex items-start space-x-4">
                              <Avatar className="h-12 w-12">
                                <AvatarFallback>
                                  {performer.name
                                    .split(' ')
                                    .map(n => n[0])
                                    .join('')}
                                </AvatarFallback>
                              </Avatar>
                              <div className="flex-1 space-y-1">
                                <div className="flex items-center justify-between">
                                  <h4 className="font-medium text-sm">{performer.name}</h4>
                                  <div className="text-sm font-semibold">
                                    <span 
                                      className={parseInt(performer.performanceScore) > 75 
                                        ? "text-emerald-500" 
                                        : parseInt(performer.performanceScore) > 50 
                                        ? "text-amber-500" 
                                        : "text-rose-500"
                                      }
                                    >
                                      {performer.performanceScore}%
                                    </span>
                                  </div>
                                </div>
                                <p className="text-sm text-muted-foreground">{performer.role}</p>
                                <div className="text-xs text-muted-foreground">
                                  <Progress 
                                    value={parseInt(performer.performanceScore)} 
                                    className="h-1 mb-1" 
                                  />
                                  <div className="flex flex-wrap gap-x-4">
                                    <span>Activities: {performer.stats.activities}</span>
                                    <span>Tasks: {performer.stats.completedTasks}</span>
                                    <span>Active Days: {performer.stats.activeDays}</span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <EmptyState 
                          message="No performance data available for this period" 
                          icon={Users} 
                        />
                      )}
                    </CardContent>
                  </Card>
                </TabsContent>

                {/* Tasks Tab */}
                <TabsContent value="tasks" className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Card>
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                          <ClipboardList className="h-5 w-5" />
                          Task Status Distribution
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="h-[300px]">
                        {data?.tasks?.taskStatus ? (
                          <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                              <Pie
                                data={[
                                  {
                                    name: 'Not Started',
                                    value: parseInt(data?.tasks?.taskStatus?.not_started || '0'),
                                  },
                                  {
                                    name: 'In Progress',
                                    value: parseInt(data?.tasks?.taskStatus?.in_progress || '0'),
                                  },
                                  {
                                    name: 'Completed',
                                    value: parseInt(data?.tasks?.taskStatus?.completed || '0'),
                                  },
                                  {
                                    name: 'Cancelled',
                                    value: parseInt(data?.tasks?.taskStatus?.cancelled || '0'),
                                  },
                                ]}
                                cx="50%"
                                cy="50%"
                                innerRadius={60}
                                outerRadius={100}
                                fill="#8884d8"
                                dataKey="value"
                                label={({name, percent}) => `${name} ${(percent * 100).toFixed(0)}%`}
                                labelLine={false}
                              >
                                {[
                                  { fill: '#dc2626' },
                                  { fill: '#2563eb' },
                                  { fill: '#16a34a' },
                                  { fill: '#71717a' },
                                ].map((entry, index) => (
                                  <Cell key={`cell-${index}`} fill={entry.fill} />
                                ))}
                              </Pie>
                              <RechartsTooltip formatter={(value) => [`${value}`, 'Count']} />
                              <Legend verticalAlign="bottom" height={36} />
                            </PieChart>
                          </ResponsiveContainer>
                        ) : (
                          <EmptyState message="No task data available" />
                        )}
                      </CardContent>
                    </Card>

                    <Card>
                      <CardHeader>
                        <CardTitle>Task Status Summary</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-4">
                          <div className="grid grid-cols-2 gap-4">
                            <div className="rounded-lg bg-green-50 p-4 dark:bg-green-900/30">
                              <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                                {data?.tasks?.taskStatus?.completed || 0}
                              </div>
                              <div className="text-sm text-green-700 dark:text-green-300">
                                Completed
                              </div>
                            </div>
                            <div className="rounded-lg bg-blue-50 p-4 dark:bg-blue-900/30">
                              <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                                {data?.tasks?.taskStatus?.in_progress || 0}
                              </div>
                              <div className="text-sm text-blue-700 dark:text-blue-300">
                                In Progress
                              </div>
                            </div>
                            <div className="rounded-lg bg-red-50 p-4 dark:bg-red-900/30">
                              <div className="text-2xl font-bold text-red-600 dark:text-red-400">
                                {data?.tasks?.taskStatus?.not_started || 0}
                              </div>
                              <div className="text-sm text-red-700 dark:text-red-300">
                                Not Started
                              </div>
                            </div>
                            <div className="rounded-lg bg-zinc-50 p-4 dark:bg-zinc-800/50">
                              <div className="text-2xl font-bold text-zinc-600 dark:text-zinc-400">
                                {data?.tasks?.taskStatus?.cancelled || 0}
                              </div>
                              <div className="text-sm text-zinc-700 dark:text-zinc-300">
                                Cancelled
                              </div>
                            </div>
                          </div>

                          <div className="border-t pt-4">
                            <div className="text-sm text-muted-foreground">
                              Total Tasks
                            </div>
                            <div className="text-2xl font-bold">
                              {data?.tasks?.taskStatus?.total || 0}
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                </TabsContent>

                {/* Additional tabs preserved but condensed */}
                {showFieldContent && (
                  <TabsContent value="field" className="space-y-4">
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    <Card>
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                          <MapPin className="h-5 w-5" />
                          Service Locations
                        </CardTitle>
                        <CardDescription>Active service locations and performance</CardDescription>
                      </CardHeader>
                      <CardContent className="h-[300px]">
                        {data?.locationMetrics?.locations?.length && data?.locationMetrics?.locations?.length > 0 ? (
                          <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={data?.locationMetrics?.locations?.slice(0, 6)}>
                              <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                              <XAxis dataKey="name" />
                              <YAxis />
                              <RechartsTooltip 
                                contentStyle={{
                                  backgroundColor: 'hsl(var(--background))',
                                  border: '1px solid hsl(var(--border))',
                                  borderRadius: '8px'
                                }}
                              />
                              <Legend />
                              <Bar 
                                dataKey="visits" 
                                name="Service Visits" 
                                fill="#8884d8"
                                radius={[4, 4, 0, 0]}
                              />
                              <Bar 
                                dataKey="completed" 
                                name="Completed Services" 
                                fill="#16a34a"
                                radius={[4, 4, 0, 0]}
                              />
                            </BarChart>
                          </ResponsiveContainer>
                        ) : (
                          <EmptyState message="No location data available" icon={MapPin} />
                        )}
                      </CardContent>
                    </Card>
                    
                    <Card>
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                          <Clock className="h-5 w-5" />
                          Field Service Efficiency
                        </CardTitle>
                        <CardDescription>Time allocation for field operations</CardDescription>
                      </CardHeader>
                      <CardContent className="h-[300px]">
                        {data?.fieldOperations?.timeAllocation ? (
                          <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                              <Pie
                                data={[
                                  { name: 'Productive', value: data.fieldOperations.timeAllocation.productive || 0 },
                                  { name: 'Travel', value: data.fieldOperations.timeAllocation.travel || 0 },
                                  { name: 'Setup', value: data.fieldOperations.timeAllocation.setup || 0 },
                                  { name: 'Waiting', value: data.fieldOperations.timeAllocation.waiting || 0 },
                                ]}
                                cx="50%"
                                cy="50%"
                                innerRadius={60}
                                outerRadius={100}
                                paddingAngle={2}
                                dataKey="value"
                                label={({name, percent}) => `${name} ${(percent * 100).toFixed(0)}%`}
                                labelLine={false}
                              >
                                {[
                                  { fill: '#16a34a' },
                                  { fill: '#2563eb' },
                                  { fill: '#f59e0b' },
                                  { fill: '#71717a' },
                                ].map((entry, index) => (
                                  <Cell key={`cell-${index}`} fill={entry.fill} />
                                ))}
                              </Pie>
                              <RechartsTooltip 
                                formatter={(value) => [`${value} hours`, '']} 
                                contentStyle={{
                                  backgroundColor: 'hsl(var(--background))',
                                  border: '1px solid hsl(var(--border))',
                                  borderRadius: '8px'
                                }}
                              />
                              <Legend verticalAlign="bottom" height={36} />
                            </PieChart>
                          </ResponsiveContainer>
                        ) : (
                          <EmptyState message="No time allocation data available" icon={Clock} />
                        )}
                      </CardContent>
                    </Card>
                  </div>
                  
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Boxes className="h-5 w-5" />
                        Equipment Usage
                      </CardTitle>
                      <CardDescription>Field equipment utilization metrics</CardDescription>
                    </CardHeader>
                    <CardContent className="h-[300px]">
                      {data?.equipmentMetrics?.usage?.length && data?.equipmentMetrics?.usage?.length > 0 ? (
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart data={data.equipmentMetrics.usage}>
                            <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                            <XAxis dataKey="name" />
                            <YAxis />
                            <RechartsTooltip 
                              contentStyle={{
                                backgroundColor: 'hsl(var(--background))',
                                border: '1px solid hsl(var(--border))',
                                borderRadius: '8px'
                              }}
                            />
                            <Legend />
                            <Bar
                              dataKey="utilization" 
                              name="Utilization %" 
                              fill="#8884d8"
                              radius={[4, 4, 0, 0]}
                            />
                            <Bar 
                              dataKey="maintenance" 
                              name="Maintenance %" 
                              fill="#f59e0b"
                              radius={[4, 4, 0, 0]}
                            />
                          </BarChart>
                        </ResponsiveContainer>
                      ) : (
                        <EmptyState message="No equipment usage data available" icon={Boxes} />
                      )}
                    </CardContent>
                  </Card>
                </TabsContent>
                )}

                {showInHouseContent && (
                  <TabsContent value="projects" className="space-y-4">
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    <Card>
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                          <Briefcase className="h-5 w-5" />
                          Project Status Overview
                        </CardTitle>
                        <CardDescription>Completion percentage by project</CardDescription>
                      </CardHeader>
                      <CardContent className="h-[300px]">
                        {data?.projects?.statusOverview?.length && data?.projects?.statusOverview?.length > 0 ? (
                          <ResponsiveContainer width="100%" height="100%">
                            <BarChart
                              data={data.projects.statusOverview}
                              layout="vertical"
                              margin={{ left: 100, right: 30, top: 10, bottom: 10 }}
                            >
                              <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                              <XAxis 
                                type="number" 
                                domain={[0, 100]}
                                unit="%"
                              />
                              <YAxis
                                type="category"
                                dataKey="name"
                                width={100}
                                tick={{ fontSize: 12 }}
                              />
                              <RechartsTooltip 
                                formatter={(value) => [`${value}%`, 'Completion']} 
                                contentStyle={{
                                  backgroundColor: 'hsl(var(--background))',
                                  border: '1px solid hsl(var(--border))',
                                  borderRadius: '8px'
                                }}
                              />
                              <Bar
                                dataKey="completion"
                                name="Completion %"
                                fill="#8884d8"
                                radius={[0, 4, 4, 0]}
                              >
                                {data.projects.statusOverview.map((entry, index) => (
                                  <Cell 
                                    key={`cell-${index}`} 
                                    fill={
                                      entry.completion >= 75 ? '#16a34a' : 
                                      entry.completion >= 50 ? '#2563eb' :
                                      entry.completion >= 25 ? '#f59e0b' : '#dc2626'
                                    } 
                                  />
                                ))}
                              </Bar>
                            </BarChart>
                          </ResponsiveContainer>
                        ) : (
                          <EmptyState message="No project status data available" icon={Briefcase} />
                        )}
                      </CardContent>
                    </Card>
                    
                    <Card>
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                          <Users className="h-5 w-5" />
                          Resource Allocation
                        </CardTitle>
                        <CardDescription>Staff and resources by project</CardDescription>
                      </CardHeader>
                      <CardContent className="h-[300px]">
                        {data?.projects?.resourceAllocation?.length && data?.projects?.resourceAllocation?.length > 0 ? (
                          <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={data.projects.resourceAllocation}>
                              <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                              <XAxis dataKey="project" />
                              <YAxis />
                              <RechartsTooltip 
                                contentStyle={{
                                  backgroundColor: 'hsl(var(--background))',
                                  border: '1px solid hsl(var(--border))',
                                  borderRadius: '8px'
                                }}
                              />
                              <Legend />
                              <Bar 
                                dataKey="staff" 
                                name="Staff" 
                                fill="#8884d8"
                                radius={[4, 0, 0, 4]}
                              />
                              <Bar 
                                dataKey="equipment" 
                                name="Equipment" 
                                fill="#82ca9d"
                                radius={[0, 4, 4, 0]}
                              />
                            </BarChart>
                          </ResponsiveContainer>
                        ) : (
                          <EmptyState message="No resource allocation data available" icon={Users} />
                        )}
                      </CardContent>
                    </Card>
                  </div>
                  
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Clock className="h-5 w-5" />
                        Project Timeline
                      </CardTitle>
                      <CardDescription>Key milestones and deadlines</CardDescription>
                    </CardHeader>
                    <CardContent>
                      {data?.projects?.timeline?.length && data?.projects?.timeline?.length > 0 ? (
                        <div className="space-y-5 max-h-[400px] overflow-y-auto pr-2">
                          {data?.projects?.timeline?.map((project, index) => (
                            <div key={index} className="space-y-2">
                              <div className="flex justify-between items-center">
                                <div>
                                  <div className="font-medium text-sm">{project.name}</div>
                                  <div className="text-xs text-muted-foreground">{project.status}</div>
                                </div>
                                <Badge 
                                  className={
                                    project.completion >= 75 ? "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300" :
                                    project.completion >= 50 ? "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300" :
                                    project.completion >= 25 ? "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300" :
                                    "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300"
                                  }
                                >
                                  {project.completion}%
                                </Badge>
                              </div>
                              <Progress value={project.completion} className="h-2" />
                              <div className="flex justify-between text-xs text-muted-foreground">
                                <div>Started: {project.startDate}</div>
                                <div>Due: {project.dueDate}</div>
                              </div>
                              {project.daysRemaining !== undefined && (
                                <div className={`text-xs ${
                                  project.daysRemaining < 0 ? 'text-rose-500' : 
                                  project.daysRemaining < 3 ? 'text-amber-500' : 
                                  'text-muted-foreground'
                                }`}>
                                  {project.daysRemaining < 0 
                                    ? `${Math.abs(project.daysRemaining)} days overdue` 
                                    : project.daysRemaining === 0 
                                      ? 'Due today'
                                      : `${project.daysRemaining} days remaining`}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <EmptyState message="No project timeline data available" icon={Clock} />
                      )}
                    </CardContent>
                  </Card>
                </TabsContent>
                )}
              </Tabs>
            </motion.div>
          </motion.div>
        </TooltipProvider>
      </Layout.Body>
      
      <Chatbot 
        currentView="dashboard"
        botName="Staffluent Assistant"
        initialMessage="👋 Welcome to Staffluent! I'm here to help with your workforce management. How can I assist you today?"
        suggestions={[
          { id: 'dashboard', text: 'Explain dashboard metrics' },
          { id: 'projects', text: 'Manage projects' },
          { id: 'team', text: 'Team management' },
          { id: 'reports', text: 'Generate reports' }
        ]}
      />
      
      <AgentsNotification />
    </Layout>
  );
}